import { useEffect } from 'react';
import * as Sentry from '@sentry/react';
import { reactRouterV6BrowserTracingIntegration } from '@sentry/react';
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';

const dsn = import.meta.env.VITE_SENTRY_DSN;
const tracePropagationTargets = [
  'localhost',
  /^https:\/\/rqgrxjbsaymzkbvrtwpb\.supabase\.co/,
];

if (typeof window !== 'undefined') {
  tracePropagationTargets.push(window.location.origin);
}

export const isSentryEnabled = Boolean(dsn);

export function initializeSentry() {
  if (!dsn) {
    return;
  }

  try {
    Sentry.init({
      dsn,
      enabled: import.meta.env.PROD,
      environment: import.meta.env.MODE,
      sendDefaultPii: true,
      enableLogs: true,
      enableMetrics: true,
      integrations: [
        reactRouterV6BrowserTracingIntegration({
          useEffect,
          useLocation,
          useNavigationType,
          createRoutesFromChildren,
          matchRoutes,
        }),
        Sentry.replayIntegration(),
        Sentry.consoleLoggingIntegration({
          levels: ['error', 'warn'],
        }),
      ],
      tracesSampleRate: import.meta.env.DEV ? 1 : 0.2,
      tracePropagationTargets,
      replaysSessionSampleRate: import.meta.env.DEV ? 1 : 0.1,
      replaysOnErrorSampleRate: 1,

      // Errors that are pure network noise or expected user mistakes —
      // not actionable by developers, no need to alert on them.
      ignoreErrors: [
        // Supabase fetch-level failure (user's network dropped, not our code)
        'FunctionsFetchError',
        'Failed to send a request to the Edge Function',
        // Raw browser fetch failure (network drop, DNS failure, etc.)
        'TypeError: Failed to fetch',
        'Failed to fetch',
        // Common browser / extension noise
        'ResizeObserver loop limit exceeded',
        'ResizeObserver loop completed with undelivered notifications',
        // Request aborted on navigation — the user left the page, not a bug
        'AbortError',
        'The user aborted a request',
      ],

      // Errors injected by browser extensions, not our code
      denyUrls: [/^chrome-extension:\/\//i, /^moz-extension:\/\//i],

      beforeSend(event, hint) {
        const error = hint?.originalException as any;
        if (!error) return event;

        const code = String(error?.code ?? '');
        const message = String(error?.message ?? '').toLowerCase();

        // Drop PostgreSQL unique constraint violations — these are expected
        // user mistakes (duplicate email, phone, enrollment number, etc.)
        // and are already handled with a friendly toast via safeErrorMessage.ts
        if (
          code === '23505' ||
          message.includes('duplicate key') ||
          message.includes('unique constraint')
        ) {
          return null;
        }

        // Edge Functions signal handled business conditions (e.g.
        // ALREADY_SUBMITTED, TOO_LATE) with an errorCode in a non-2xx JSON
        // body. invokeEdgeFunction attaches that body as responseBody before
        // capturing; the UI already renders the right state for these.
        if (error?.responseBody?.errorCode) {
          return null;
        }

        return event;
      },
    });

    Sentry.instrumentSupabaseClient(supabase);

    Sentry.logger.info('Sentry client initialized', {
      environment: import.meta.env.MODE,
    });

    Sentry.metrics.count('app.boot', 1, {
      attributes: {
        environment: import.meta.env.MODE,
      },
    });
  } catch (error) {
    console.warn('Sentry initialization failed. Continuing without Sentry.', error);
  }
}

export { Sentry };
